import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { DecimalPipe } from '@angular/common';
import { Router } from '@angular/router';
import { TenantService } from '@/cloud/services/tenant.service';
import { AssistantService } from '@/cloud/services/assistant.service';
import { RoomTile, RoomsBlock } from '@/cloud/models/assistant.models';
import { TranslationService } from '@/layout/service/translation.service';
import { fill } from '@/layout/service/assistant.translations';

/** Room tiles: 30-day rating as stars, devices in the room and how many are offline. */
@Component({
    selector: 'app-assistant-room-tiles',
    standalone: true,
    imports: [DecimalPipe],
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
        @if (block().title) {
            <div class="ab-title">{{ block().title }}</div>
        }
        <div class="room-grid">
            @for (r of block().rooms; track r.locationId) {
                <button type="button" class="room-tile" [attr.data-tone]="tone(r)" (click)="open(r)">
                    <span class="room-name">{{ r.name }}</span>
                    @if (r.namespaceName && serviceDesk()) {
                        <span class="room-tenant">{{ r.namespaceName }}</span>
                    }
                    @if (r.rating !== null) {
                        <span class="stars" [attr.aria-label]="(r.rating | number: '1.1-1') + ' out of 5'">
                            @for (s of [1, 2, 3, 4, 5]; track s) {
                                <i class="pi" [class.pi-star-fill]="r.rating! >= s - 0.25" [class.pi-star]="r.rating! < s - 0.25" aria-hidden="true"></i>
                            }
                            <span class="rating">{{ r.rating | number: '1.1-1' }}</span>
                            <span class="muted">· {{ fillT('assistant.rooms.ratings', { count: r.ratingCount }) }}</span>
                        </span>
                    } @else {
                        <span class="muted">{{ t()('assistant.rooms.noRatings') }}</span>
                    }
                    <span class="room-meta">
                        <span><i class="pi pi-server" aria-hidden="true"></i>{{ fillT('assistant.rooms.devices', { count: r.devices }) }}</span>
                        @if (r.offline > 0) {
                            <span class="danger"><i class="pi pi-times-circle" aria-hidden="true"></i>{{ fillT('assistant.rooms.offline', { count: r.offline }) }}</span>
                        }
                    </span>
                </button>
            }
        </div>
    `,
    styleUrl: './blocks.scss'
})
export class AssistantRoomTilesComponent {
    readonly block = input.required<RoomsBlock>();
    private router = inject(Router);
    private tenants = inject(TenantService);
    private assistant = inject(AssistantService);
    readonly t = inject(TranslationService).t;
    readonly serviceDesk = computed(() => this.assistant.scope() === 'serviceDesk');

    tone(r: RoomTile): string {
        if (r.offline > 0) return 'danger';
        if (r.rating !== null && r.rating < 3) return 'warn';
        if (r.rating !== null && r.rating >= 4.2) return 'success';
        return 'info';
    }

    fillT(key: string, values: Record<string, number>): string {
        return fill(this.t()(key), values);
    }

    open(r: RoomTile): void {
        if (r.namespaceId !== this.tenants.selectedTenantId()) this.tenants.select(r.namespaceId);
        this.router.navigate(['/location', r.locationId]);
    }
}
